/*
 * Clientul de api: singurul loc din aplicatie care stie de `fetch`.
 *
 * Ecranele nu cheama niciodata fetch. Motivul nu e curatenia, ci ca regulile de
 * mai jos - antetul de autorizare, refresh-ul la 401, timeoutul, despachetarea
 * plicului {status,data} - trebuie sa se aplice la fiecare cerere, iar o
 * regula pe care fiecare ecran o rescrie e o regula care intr-un ecran lipseste.
 */

import { config } from './config';
import { t } from '../lib/i18n';
import { isNative } from '../lib/platform';
import { ApiError, codeForStatus, messageForStatus } from './errors';
import * as session from './session';
import type { ApiEnvelope, AuthTokens } from './types';

/* Chemat cand sesiunea e definitiv pierduta. Radacina Reactului il inlocuieste
   cu navigarea catre login; pana atunci nu face nimic, ca stratul de api sa
   poata fi folosit si fara interfata. */
let onSessionLost: () => void = () => {};

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  /** Implicit true. `false` pentru rutele publice din contract. */
  auth?: boolean;
  query?: Record<string, QueryValue>;
  body?: unknown;
  /**
   * Fisiere, trimise ca `multipart/form-data`.
   *
   * Separat de `body` dinadins: cele doua nu pot pleca in aceeasi cerere, si
   * un singur camp care poate fi si una si alta ar fi cerut ca fiecare loc sa
   * se intrebe intai ce a primit.
   */
  form?: FormData;
  /** Semnalul ecranului, ca un raspuns intarziat sa nu mai fie asteptat. */
  signal?: AbortSignal;
  /** `false` pe refresh si pe logout: nici una din ele nu are voie sa ceara refresh. */
  allowRefresh?: boolean;
  /**
   * true = cererea duce cookie-urile api-ului (`credentials: 'include'`). Numai
   * refresh-ul si logout-ul, si numai cand refresh tokenul e in cookie httpOnly.
   * Restul cererilor raman fara cookie: autentificarea lor e antetul.
   */
  withCookies?: boolean;
}

/* ---------------------------------------------------------------- adresa */

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = config.apiBaseUrl + path;
  if (!query) return url;

  const pairs = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    /* Valorile goale nu se trimit: pentru un filtru, "?q=" si lipsa lui "q" nu
       inseamna acelasi lucru, iar contractul nu cere trimiterea lor. */
    if (value === null || value === undefined || value === '') continue;
    pairs.set(key, String(value));
  }

  const search = pairs.toString();
  return search ? `${url}?${search}` : url;
}

/* --------------------------------------------------------------- raspuns */

interface ParsedResponse {
  status: number;
  /** null cand corpul e gol; `notJson` cand a venit altceva decat JSON. */
  body: unknown;
  notJson: boolean;
}

async function read(response: Response): Promise<ParsedResponse> {
  const text = await response.text();
  if (!text) return { status: response.status, body: null, notJson: false };
  try {
    return { status: response.status, body: JSON.parse(text) as unknown, notJson: false };
  } catch {
    return { status: response.status, body: null, notJson: true };
  }
}

function isEnvelope(body: unknown): body is ApiEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'status' in body;
}

function errorFrom(parsed: ParsedResponse, fallbackMessage?: string): ApiError {
  const body = isEnvelope(parsed.body) && parsed.body.status === 'ERROR' ? parsed.body : null;
  const message = body?.message ?? fallbackMessage ?? messageForStatus(parsed.status);
  const code = body?.code ?? codeForStatus(parsed.status);
  const fields = body?.data?.fields ?? null;
  return new ApiError(message, code, parsed.status, fields);
}

/*
 * Cheia, nu textul gata tradus.
 *
 * O constanta de modul se evalueaza O SINGURA DATA, la incarcare. Daca aici ar
 * sta `t(...)`, mesajul ar ramane inghetat in limba de la pornire, iar dupa o
 * schimbare de limba omul ar primi in continuare propozitia veche. Se traduce
 * la fiecare folosire, cu `sessionEnded()`.
 */
const SESSION_ENDED_KEY = 'Your session has ended. Please sign in again.';

function sessionEnded(): string {
  return t(SESSION_ENDED_KEY);
}

/* ---------------------------------------------------------- refresh unic */

/*
 * Un singur refresh in zbor, oricate cereri ar lua 401 in acelasi timp.
 *
 * Fara asta, trei ecrane care se incarca odata trimit trei refresh-uri; al
 * doilea si al treilea folosesc un refresh_token deja consumat, primesc
 * `refresh_expired` si dau omul afara desi sesiunea era buna. Bug-ul apare doar
 * cand tokenul expira exact intre doua ecrane, adica niciodata la testare si
 * mereu la client.
 */
let refreshInFlight: Promise<void> | null = null;

function refreshOnce(): Promise<void> {
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function runRefresh(): Promise<void> {
  const viaCookie = session.usesRefreshCookie();
  const refreshToken = session.getRefreshToken();
  if (!viaCookie && !refreshToken) throw new ApiError(sessionEnded(), 'refresh_expired', 401);

  /* auth:false si allowRefresh:false - cererea de refresh nu poate declansa un
     refresh, altfel doua tokenuri moarte produc o bucla infinita.

     Prin cookie, corpul nu poarta niciun token: il duce browserul, iar
     JavaScript-ul nu l-a vazut niciodata. */
  const tokens = await send<AuthTokens>('POST', '/shop/auth/refresh', {
    auth: false,
    allowRefresh: false,
    withCookies: viaCookie,
    body: viaCookie
      ? { ...refreshTransport() }
      : { refresh_token: refreshToken, ...refreshTransport() },
  });
  session.adopt(tokens);
}

/**
 * Cererea catre api sa tina refresh tokenul in cookie httpOnly.
 *
 * Numai cu VITE_REFRESH_COOKIE=1 (vezi config.ts) si numai pe web: in carcasa
 * nativa pagina e servita de pe https://localhost, deci
 * un cookie al api-ului ar fi al unui site strain si n-ar ajunge inapoi. Un api
 * care nu stie de camp il ignora si raspunde ca pana acum (refresh_token in
 * corp) - clientul se uita la `refresh_cookie` din raspuns, nu la ce a cerut.
 */
export function refreshTransport(): { refresh_transport?: 'cookie' } {
  return config.refreshCookie && !isNative ? { refresh_transport: 'cookie' } : {};
}

/* ---------------------------------------------------------------- cererea */

function endSession(): void {
  session.clear();
  onSessionLost();
}

/**
 * `attempt` numara reluarile. Contractul spune ca la 401 se incearca refresh o
 * singura data; `attempt > 0` inseamna ca s-a incercat deja si nu se mai reia.
 */
async function send<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
  attempt = 0,
): Promise<T> {
  const needsAuth = options.auth !== false;
  const token = session.getToken();

  if (needsAuth && token === null) {
    /* Dupa F5 jwt-ul nu mai e nicaieri (sta numai in memorie), dar exista cu ce
       cere unul nou: se cere intai, apoi pleaca cererea. */
    if (options.allowRefresh !== false && attempt === 0 && session.canRefresh()) {
      try {
        await refreshOnce();
      } catch {
        endSession();
        throw new ApiError(sessionEnded(), 'unauthenticated', 401);
      }
      return send<T>(method, path, options, attempt + 1);
    }
    /* Nu are rost sa plece o cerere fara token doar ca sa se intoarca 401. */
    endSession();
    throw new ApiError(sessionEnded(), 'unauthenticated', 401);
  }

  /* Generatia de la plecare. Daca la intoarcerea cu 401 s-a schimbat, tokenul a
     fost deja improspatat de alta cerere si aceasta se reia cu cel nou, fara sa
     mai ceara un refresh pe un refresh_token deja consumat. */
  const generationAtSend = session.getGeneration();

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  /*
   * La `multipart` NU se pune `Content-Type` cu mana. Browserul il scrie
   * singur, si adauga in el granita care desparte fisierele - o valoare pe care
   * n-o stim aici. Scris de noi, serverul primeste un antet fara granita si nu
   * gaseste niciun fisier in ce a venit.
   */
  if (needsAuth && token !== null) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  /* Steag propriu si `abort()` fara motiv: `abort(reason)` face ca fetch sa
     respinga chiar cu motivul dat, nu cu un DOMException, deci verificarea de
     mai jos n-ar mai recunoaste anularea si orice timeout ar fi raportat ca
     eroare de retea. */
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, config.requestTimeoutMs);

  /* Anularea venita din ecran (componenta demontata) se propaga la fetch, dar
     nu se confunda cu timeoutul: prima nu se arata nimanui, a doua da. */
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  let parsed: ParsedResponse;
  try {
    const response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      /* Autentificarea e prin antet, nu prin cookie: fara credentiale, cererea
         cross-origin nu are nevoie de Access-Control-Allow-Credentials. Singura
         exceptie e refresh tokenul tinut in cookie httpOnly (vezi withCookies). */
      credentials: options.withCookies === true ? 'include' : 'omit',
      cache: 'no-store',
      signal: controller.signal,
      ...(options.form !== undefined
        ? { body: options.form }
        : (options.body !== undefined ? { body: JSON.stringify(options.body) } : {})),
    });
    parsed = await read(response);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    if (timedOut) {
      throw new ApiError(t('The service did not answer in time. Please try again.'), 'timeout', 0);
    }
    /* Aici intra si CORS blocat, si DNS gresit, si api oprit: browserul nu le
       distinge, deci nici mesajul nu pretinde ca le distinge. */
    throw new ApiError(
      t('Cannot reach the service. Check your connection and try again.'),
      'network_error',
      0,
    );
  } finally {
    window.clearTimeout(timer);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }

  /* ---- 401: "mai incearca", distinct de 403 "nu tu" (contract, §0) ---- */
  if (parsed.status === 401 && needsAuth && options.allowRefresh !== false && attempt === 0) {
    if (session.getGeneration() !== generationAtSend) {
      return send<T>(method, path, options, attempt + 1);
    }
    if (!session.canRefresh()) {
      endSession();
      throw errorFrom(parsed, sessionEnded());
    }
    try {
      await refreshOnce();
    } catch {
      endSession();
      throw new ApiError(sessionEnded(), 'unauthenticated', 401);
    }
    return send<T>(method, path, options, attempt + 1);
  }

  if (parsed.status === 401 && needsAuth) {
    endSession();
    throw errorFrom(parsed, sessionEnded());
  }

  if (parsed.notJson) {
    throw new ApiError(
      t('The service answered with something that is not JSON. It may be misconfigured.'),
      'bad_response',
      parsed.status,
    );
  }

  if (isEnvelope(parsed.body) && parsed.body.status === 'ERROR') throw errorFrom(parsed);
  if (parsed.status < 200 || parsed.status >= 300) throw errorFrom(parsed);

  /* Contractul garanteaza {"status":"SUCCESS","data":{...}}. Un corp gol pe 2xx
     (logout, de exemplu) se intoarce ca obiect gol, ca apelantul sa nu trateze
     null pe langa tipul asteptat. */
  if (isEnvelope(parsed.body) && parsed.body.status === 'SUCCESS') {
    return (parsed.body.data ?? {}) as T;
  }
  return {} as T;
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => send<T>('GET', path, options),
  post: <T>(path: string, options?: RequestOptions) => send<T>('POST', path, options),
  put: <T>(path: string, options?: RequestOptions) => send<T>('PUT', path, options),
  delete: <T>(path: string, options?: RequestOptions) => send<T>('DELETE', path, options),
};
